import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseFormReturn, Controller } from 'react-hook-form';
import { X } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { DocumentSchema } from '../../documents/types';
import { cn } from '../../../lib/utils';
import { CoAuthorPicker } from './AuthorPicker';
import type { CoAuthor } from '../../../services/api/policyService';
import { AttachmentUploadDialog } from '../../files/AttachmentUploadDialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { DatePicker } from '../../ui/date-picker';

export interface PolicyMetadata {
    title: string;
    effectiveDate: string;
    expiryDate: string;
    tags: string[];
    coAuthors: CoAuthor[];
    attachments: string[];
    requireApproval: boolean;
}

interface PolicyMetadataBarProps {
    schema: DocumentSchema;
    metaForm: UseFormReturn<PolicyMetadata>;
    departmentUsers: Array<{ _id: string; name: string; role?: string }>;
    usersLoading: boolean;
    expiryDateHint?: string;
}

export function PolicyMetadataBar({
    schema,
    metaForm,
    departmentUsers,
    usersLoading,
    expiryDateHint,
}: PolicyMetadataBarProps) {
    const { t } = useTranslation();
    const [tagInput, setTagInput] = useState('');

    const watchedTags = metaForm.watch('tags') ?? [];
    const watchedCoAuthors = metaForm.watch('coAuthors') ?? [];

    const addTag = () => {
        const tag = tagInput.trim();
        const current = metaForm.getValues('tags');
        const hasMatchingTag = current.some(
            (existingTag) => existingTag.toLowerCase() === tag.toLowerCase()
        );

        if (tag && !hasMatchingTag) {
            metaForm.setValue('tags', [...current, tag]);
            setTagInput('');
        }
    };

    const removeTag = (tag: string) => {
        metaForm.setValue(
            'tags',
            metaForm.getValues('tags').filter((t) => t !== tag)
        );
    };

    return (
        <div className="space-y-5">
            <div className="space-y-1.5">
                <Label>
                    {t('departmentPolicies.metadata.policyTitle')} <span className="text-destructive">*</span>
                </Label>
                <Input
                    {...metaForm.register('title', { required: true })}
                    placeholder={schema.title || t('departmentPolicies.metadata.untitledPolicy')}
                    className={cn(
                        'h-9 text-sm font-medium',
                        metaForm.formState.errors.title &&
                            'border-destructive'
                    )}
                />
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                    <div className="flex min-h-5 items-center">
                        <Label>
                            {t('departmentPolicies.metadata.effectiveDate')} <span className="text-destructive">*</span>
                        </Label>
                    </div>
                    <Controller
                        control={metaForm.control}
                        name="effectiveDate"
                        rules={{ required: true }}
                        render={({ field }) => (
                            <DatePicker
                                value={field.value}
                                onChange={field.onChange}
                                placeholder={t('departmentPolicies.metadata.pickEffectiveDate')}
                                className={cn(
                                    'h-9 text-sm',
                                    metaForm.formState.errors.effectiveDate &&
                                        'border-destructive'
                                )}
                            />
                        )}
                    />
                </div>
                <div className="space-y-1.5">
                    <div className="flex min-h-5 items-center gap-2">
                        <Label className="shrink-0">
                            {t('departmentPolicies.metadata.expiryDate')} <span className="text-destructive">*</span>
                        </Label>
                        {expiryDateHint && (
                            <Badge
                                variant="outline"
                                className="h-5 px-1.5 py-0 text-[11px] font-medium leading-none text-muted-foreground"
                            >
                                {expiryDateHint}
                            </Badge>
                        )}
                    </div>
                    <Controller
                        control={metaForm.control}
                        name="expiryDate"
                        rules={{ required: true }}
                        render={({ field }) => (
                            <DatePicker
                                value={field.value}
                                onChange={field.onChange}
                                placeholder={t('departmentPolicies.metadata.pickExpiryDate')}
                                className={cn(
                                    'h-9 text-sm',
                                    metaForm.formState.errors.expiryDate &&
                                        'border-destructive'
                                )}
                            />
                        )}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 items-start">
                <div className="space-y-1.5">
                    <Label>{t('departmentPolicies.metadata.tags')}</Label>
                    <Input
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                addTag();
                            }
                        }}
                        placeholder={t('departmentPolicies.metadata.addTag')}
                        className="h-9 py-1.5 text-sm w-full"
                    />
                    {watchedTags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-0.5">
                            {watchedTags.map((tag) => (
                                <Badge
                                    key={tag}
                                    variant="secondary"
                                    className="gap-1 pr-1 text-xs h-6 font-normal bg-zinc-200 text-zinc-800 border-0 hover:bg-zinc-200"
                                >
                                    {tag}
                                    <button
                                        onClick={() => removeTag(tag)}
                                        className="hover:bg-muted-foreground/20 rounded-full p-0.5"
                                    >
                                        <X className="h-2.5 w-2.5" />
                                    </button>
                                </Badge>
                            ))}
                        </div>
                    )}
                </div>
                <div className="space-y-1.5">
                    <Label>
                        {t('departmentPolicies.metadata.authors')}
                        {usersLoading && (
                            <span className="ml-1.5 text-xs font-normal text-muted-foreground/70">
                                {t('departmentPolicies.metadata.loading')}
                            </span>
                        )}
                    </Label>
                    <CoAuthorPicker
                        departmentUsers={departmentUsers}
                        value={watchedCoAuthors}
                        onChange={(next) => metaForm.setValue('coAuthors', next)}
                        loading={usersLoading}
                    />
                </div>
            </div>

            <div className="space-y-1.5">
                <Label>{t('departmentPolicies.metadata.attachments')}</Label>
                <AttachmentUploadDialog
                    defaultFiles={metaForm.getValues('attachments') ?? []}
                    onFilesSelected={(fileIds) => metaForm.setValue('attachments', fileIds)}
                    ownerType="department"
                    allowMultiple
                    allowedTypes={{ document: true, image: true, other: true }}
                />
            </div>

            <div className="flex items-center justify-between gap-3 pt-1">
                <div className="space-y-0.5">
                    <Label htmlFor="require-approval-toggle">
                        {t('departmentPolicies.metadata.requireApproval')}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                        {t('departmentPolicies.metadata.requireApprovalDescription')}
                    </p>
                </div>
                <Controller
                    control={metaForm.control}
                    name="requireApproval"
                    render={({ field }) => (
                        <Switch
                            id="require-approval-toggle"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                        />
                    )}
                />
            </div>
        </div>
    );
}
